import { type TFaqNested, type TFaqSection } from '../types/faq.types';

/**
 * Рекурсивно сортирует статьи по позиции, при равной позиции - по имени.
 * Сортировка применяется на всех уровнях вложенности.
 */
export const sortArticlesRecursively = (
  articles: TFaqNested[]
): TFaqNested[] => {
  return articles
    .sort(
      (a, b) =>
        Number(a.position || 0) - Number(b.position || 0) ||
        a.name.localeCompare(b.name)
    )
    .map((article) => ({
      ...article,
      children: sortArticlesRecursively(article.children)
    }));
};

/**
 * Рекурсивно присваивает номера порядкового отображения (например, "1.2.3").
 * Номера присваиваются только активным элементам (не disabled).
 * Если родительский элемент disabled, дочерние элементы не получают номера.
 */
export const assignChildrenOrderNo = (
  faqItem: TFaqNested,
  prevOrderNo = ''
): TFaqNested => {
  const orderNo = !faqItem.status.isDisabled ? prevOrderNo : '';

  let order = 1;
  const children = faqItem.children.map((child) => {
    if (!child.status.isDisabled && orderNo) {
      return assignChildrenOrderNo(child, `${orderNo}.${order++}`);
    } else {
      return assignChildrenOrderNo(child, '');
    }
  });

  return {
    ...faqItem,
    orderNo,
    children
  };
};

/**
 * Подсчитывает количество точек и не-точек в строке номера (например, "1.2.3" -> [2, 3]).
 * Используется для расчета ширины отображения номеров в UI.
 */
export const countDotsAndNotDots = (str: string): [number, number] => {
  if (!str) {
    return [0, 0];
  }

  let dotCount = 0;
  let nonDotCount = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '.') {
      dotCount++;
    } else {
      nonDotCount++;
    }
  }
  return [dotCount, nonDotCount];
};

/**
 * Рекурсивно вычисляет максимальную длину номеров на каждом уровне вложенности.
 * Результат используется для выравнивания отображения номеров в списке.
 */
export const assignChildrenMaxOrderNoLength = (
  faqItem: TFaqNested,
  maxDotsAndNotDotsOnLevel: [number, number]
): TFaqNested => {
  const children = faqItem.children.map((child) =>
    assignChildrenMaxOrderNoLength(
      child,
      countDotsAndNotDots(child.orderNo || '')
    )
  );

  return {
    ...faqItem,
    children,
    maxDotsAndNotDotsOnLevel
  };
};

/**
 * Рекурсивно фильтрует статьи по поисковому запросу.
 * Элемент показывается, если:
 * - его название содержит запрос И элемент активен (или это админ-панель), ИЛИ
 * - у него есть дочерние элементы, соответствующие запросу
 * Это позволяет показывать родительские элементы, даже если они не совпадают с запросом,
 * но имеют соответствующие дочерние элементы.
 */
const filterFaqArticleBySearch = (
  faqItem: TFaqNested,
  searchValue: string,
  isAdminPage = false
): TFaqNested | null => {
  if (!searchValue) {
    return faqItem;
  }

  const searchLower = searchValue.toLowerCase();
  const currentItemMatches =
    faqItem.name?.toLowerCase().includes(searchLower) ?? false;

  const filteredChildren = faqItem.children
    .map((child) => filterFaqArticleBySearch(child, searchValue, isAdminPage))
    .filter((child): child is TFaqNested => child !== null);

  const hasMatchingChildren = filteredChildren.length > 0;

  if (
    (currentItemMatches &&
      (isAdminPage ||
        (!faqItem.status.isDisabled && !faqItem.status.isSelfDisabled))) ||
    hasMatchingChildren
  ) {
    return {
      ...faqItem,
      children: filteredChildren,
      status: {
        ...faqItem.status,
        hasMatchingChildren
      }
    };
  }

  return null;
};

const filterFaqArticleByPosition = (
  faqItem: TFaqNested,
  filterPositionUuid: string,
  isAdminPage = false
): TFaqNested | null => {
  if (!filterPositionUuid) {
    return faqItem;
  }

  // Сначала рекурсивно фильтруем дочерние элементы
  const filteredChildren = faqItem.children
    .map((child) =>
      filterFaqArticleByPosition(child, filterPositionUuid, isAdminPage)
    )
    .filter((child): child is TFaqNested => child !== null);

  const hasMatchingChildren = filteredChildren.length > 0;
  const currentItemMatches =
    faqItem.faqPagesPositions.length === 0 ||
    faqItem.faqPagesPositions.includes(filterPositionUuid);

  // Для админ-панели показываем все элементы, даже без доступа по позиции
  if (isAdminPage) {
    if (currentItemMatches || hasMatchingChildren) {
      return {
        ...faqItem,
        children: filteredChildren,
        status: {
          ...faqItem.status,
          hasMatchingChildren,
          isDisabledByPosition: !currentItemMatches && hasMatchingChildren
        }
      };
    }
    return null;
  }

  // Для пользовательской страницы: показываем элемент, если:
  // 1. Элемент соответствует позиции И активен, ИЛИ
  // 2. Есть активные дочерние элементы (даже если сам элемент не соответствует позиции или отключен)
  const shouldShowItem =
    (currentItemMatches &&
      !faqItem.status.isDisabled &&
      !faqItem.status.isSelfDisabled) ||
    hasMatchingChildren;

  if (shouldShowItem) {
    return {
      ...faqItem,
      children: filteredChildren,
      status: {
        ...faqItem.status,
        hasMatchingChildren,
        isDisabledByPosition: !currentItemMatches && hasMatchingChildren
      }
    };
  }

  return null;
};

/**
 * Применяет фильтры к секциям FAQ: сначала по позиции, затем по поисковому запросу.
 * Фильтры применяются последовательно: сначала позиция, потом поиск.
 * Секции без статей после фильтрации удаляются.
 */
export const filterFaqSections = (
  faqSections: TFaqSection[],
  filters: {
    searchValue?: string;
    filterPositionUuid?: string;
    isAdminPage?: boolean;
  }
): TFaqSection[] => {
  const {
    searchValue = '',
    filterPositionUuid = '',
    isAdminPage = false
  } = filters;

  return (
    faqSections
      .map((faqSection) => ({
        ...faqSection,
        articles: faqSection.articles
          .map((article) =>
            filterFaqArticleByPosition(article, filterPositionUuid, isAdminPage)
          )
          .filter((article): article is TFaqNested => article !== null)
          .map((article) =>
            filterFaqArticleBySearch(article, searchValue, isAdminPage)
          )
          .filter((article): article is TFaqNested => article !== null)
      }))
      // Удаляем секции, в которых не осталось статей
      .filter((faqSection) => faqSection.articles.length > 0)
  );
};

/**
 * Находит статью по UUID во всех секциях, включая вложенные дочерние элементы.
 * Возвращает null, если статья не найдена.
 */
export const findFaqNestedByUuid = (
  sections: TFaqSection[],
  uuid: string
): TFaqNested | null => {
  for (const section of sections) {
    for (const article of section.articles) {
      const found = findNestedRecursive(article, uuid);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/**
 * Рекурсивно ищет статью по UUID в дереве вложенных элементов.
 * Проверяет текущий элемент и все его дочерние элементы.
 */
const findNestedRecursive = (
  faqItem: TFaqNested,
  uuid: string
): TFaqNested | null => {
  if (faqItem.uuid === uuid) {
    return faqItem;
  }
  for (const child of faqItem.children) {
    const result = findNestedRecursive(child, uuid);
    if (result) {
      return result;
    }
  }
  return null;
};
